import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Box } from 'components';
import Typography from '@mui/material/Typography';
import FormLabel from '@mui/material/FormLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { Spinner } from 'components/shared';
import FlexRow from '../../shared/FlexRow';
import Button from '../../shared/Button';
import { EMPLOYER_LOCATIONS, EMPLOYEES_BY_LOCATION } from '../../../api/queries';
import {
  IOnboarding,
  IOnboardingAssignment,
  IOnboardingDocumentAssociations,
} from '../../../api/types/queries/onboarding.types';

export interface IAssignmentFormValues {
  employeeId: number;
  onboardingDocumentIds: string[];
}

interface ILocation {
  id: number;
  name: string;
}

interface ILocationsResp {
  employerLocations: ILocation[];
}

interface IEmployeeRow {
  id: number;
  employeeId: number;
  name: string;
  imageUrl?: string;
  currentJobType?: string;
  location?: string;
}

interface IEmployeesByLocationResp {
  employeesByLocation: {
    results: IEmployeeRow[];
  };
}

const AssignmentForm = ({
  onboarding,
  saving,
  handleSave,
  handleCancel,
}: {
  onboarding: IOnboarding;
  saving?: boolean;
  handleSave: (values: IAssignmentFormValues) => void;
  handleCancel: () => void;
}) => {
  const [locationId, setLocationId] = useState<number | ''>('');
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  const [selectedDocumentIds, setSelectedDocumentIds] =
    useState<string[]>([]);

  const [
    fetchLocations,
    { data: locationsData, loading: locationsLoading },
  ] = useLazyQuery<ILocationsResp>(EMPLOYER_LOCATIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const [
    fetchEmployees,
    { data: employeesData, loading: employeesLoading },
  ] = useLazyQuery<IEmployeesByLocationResp>(EMPLOYEES_BY_LOCATION, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locationId) {
      fetchEmployees({
        variables: { locationId: Number(locationId), limit: 200 },
      });
      setEmployeeId('');
    }
  }, [locationId]);

  const documentAssociations: IOnboardingDocumentAssociations[] =
    onboarding?.documentAssociations || [];
  const existingAssignments: IOnboardingAssignment[] =
    onboarding?.assignments || [];

  const alreadyAssignedDocIds = useMemo(() => {
    if (!employeeId) return new Set<number | string>();
    return new Set(
      existingAssignments
        .filter((a) => a.employeeId === Number(employeeId))
        .map((a) => a.onboardingDocumentId),
    );
  }, [employeeId, existingAssignments]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeId || selectedDocumentIds.length === 0) return;
    handleSave({
      employeeId: Number(employeeId),
      onboardingDocumentIds: selectedDocumentIds,
    });
  };

  const locations = locationsData?.employerLocations || [];
  const employees = employeesData?.employeesByLocation?.results || [];

  return (
    <Box>
      <form onSubmit={onSubmit}>
        <Typography
          variant={'h2'}
          sx={{
            mb: (theme) => theme.spacing(2),
            mt: (theme) => theme.spacing(2),
          }}
        >
          New Assignment
        </Typography>
        <FlexRow>
          <FormLabel sx={{ minWidth: 320 }}>
            <Typography variant={'h3'}>Location:</Typography>
            {locationsLoading ? (
              <Spinner size={20} />
            ) : (
              <Select
                value={locationId}
                onChange={(e) =>
                  setLocationId(Number(e.target.value))
                }
                fullWidth
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select a location
                </MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormLabel>
        </FlexRow>
        <FlexRow>
          <FormLabel sx={{ minWidth: 320 }}>
            <Typography variant={'h3'}>Employee:</Typography>
            {employeesLoading ? (
              <Spinner size={20} />
            ) : (
              <Select
                value={employeeId}
                onChange={(e) =>
                  setEmployeeId(Number(e.target.value))
                }
                fullWidth
                disabled={!locationId}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {locationId
                    ? 'Select an employee'
                    : 'Pick a location first'}
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.employeeId}>
                    {emp.name}
                    {emp.currentJobType
                      ? ` — ${emp.currentJobType}`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormLabel>
        </FlexRow>
        <FlexRow>
          <FormLabel sx={{ minWidth: 320 }}>
            <Typography variant={'h3'}>Documents:</Typography>
            <Select
              multiple
              value={selectedDocumentIds}
              onChange={(e) =>
                setSelectedDocumentIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : (e.target.value as string[]),
                )
              }
              fullWidth
              disabled={!employeeId}
              renderValue={(selected) => {
                const ids = selected as string[];
                if (ids.length === 0) {
                  return employeeId
                    ? 'Select one or more documents'
                    : 'Pick an employee first';
                }
                return documentAssociations
                  .filter((d) =>
                    ids.includes(d.onboardingDocumentId),
                  )
                  .map((d) => d.document?.name)
                  .filter(Boolean)
                  .join(', ');
              }}
              displayEmpty
            >
              {documentAssociations.map((d) => {
                const docId = d.onboardingDocumentId;
                const alreadyAssigned =
                  alreadyAssignedDocIds.has(docId) ||
                  alreadyAssignedDocIds.has(Number(docId));
                return (
                  <MenuItem
                    key={d.id}
                    value={docId}
                    disabled={alreadyAssigned}
                  >
                    <Checkbox
                      checked={selectedDocumentIds.includes(docId)}
                    />
                    <ListItemText
                      primary={d.document?.name}
                      secondary={
                        alreadyAssigned ? 'Already assigned' : undefined
                      }
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormLabel>
        </FlexRow>
        <FlexRow>
          <Button
            variant={'outlined-primary'}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant={'filled-primary'}
            type={'submit'}
            disabled={
              !employeeId ||
              selectedDocumentIds.length === 0 ||
              saving
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </FlexRow>
      </form>
    </Box>
  );
};

export default AssignmentForm;
